import {
  BulkDeleteMutation,
  DeleteVideoMutation,
  useBulkDeleteMutation,
  useDeleteVideoMutation,
  useEditVideoMutation,
} from 'generated/graphql';
import { Video } from './library.domain';
import { trackEvent } from '@services/tracking';
import { typeToLabel } from '../helpers';
import { FetchResult } from '@apollo/client';

export type UpdatableVideo = {
  id: string;
} & Partial<Pick<Video, 'name' | 'description' | 'type'>> &
  Pick<Video, 'videoSourceType'> & {
    thumbnailTimestamp?: number;
  };

export type DeleteVideo = {
  id: string;
} & Pick<Video, 'videoSourceType'>;

export type UseVideosWriteModelParams = { influencerId: number };
export type VideosWriteModel = {
  updateVideo: (
    video: UpdatableVideo,
    previousParameters: Pick<
      UpdatableVideo,
      'thumbnailTimestamp' | 'name' | 'description'
    >,
  ) => void;
  deleteVideo: (data: DeleteVideo) => Promise<FetchResult<DeleteVideoMutation>>;
  bulkDelete: (
    videoIds: string[],
    folderIds: number[],
  ) => Promise<FetchResult<BulkDeleteMutation>>;
};

export const useVideosWriteModel = (
  p: UseVideosWriteModelParams,
): VideosWriteModel => {
  const [editVideo] = useEditVideoMutation();
  const [deleteVideoMutation] = useDeleteVideoMutation();
  const [bulkDeleteMutation] = useBulkDeleteMutation();
  const updateVideo = (
    video: UpdatableVideo,
    previousParameters: Pick<
      UpdatableVideo,
      'thumbnailTimestamp' | 'name' | 'description'
    >,
  ) => {
    (['thumbnailTimestamp', 'name', 'description'] as const).map((field) => {
      if (previousParameters[field] === video[field]) {
        return;
      }

      const fieldName =
        field === 'thumbnailTimestamp'
          ? 'Thumbnail'
          : field === 'name'
          ? 'Title'
          : 'Description';

      trackEvent({
        type: `${fieldName} Edited`,
        payload: {
          'File Type': 'Video',
          'Edited From': 'Library',
          'Editing During': 'Post-upload',
          'File Length': typeToLabel(video.type),
        },
      });
    });

    return editVideo({ variables: { creatorId: p.influencerId, ...video } });
  };

  const deleteVideo = ({ id, videoSourceType }: DeleteVideo) =>
    deleteVideoMutation({
      variables: { creatorId: p.influencerId, id, videoSourceType },
    });
  const bulkDelete = (videoIds: string[], folderIds: number[]) =>
    bulkDeleteMutation({
      variables: { creatorId: p.influencerId, videoIds, folderIds },
    });

  return { updateVideo, deleteVideo, bulkDelete };
};
